// The app reads the current rights from /auth/me on every load, but the
// service APIs trust the rights stored in the access token at login. When an
// admin changes this user's tags, both differ until the token is renewed.

const decodePayload = (token) => {
  const part = String(token || '').split('.')[1];
  if (!part) return null;
  const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
  return JSON.parse(atob(base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')));
};

/** True when the token's rights are not exactly the current rights. */
export const tokenRightsDiffer = (token, currentPermissions) => {
  if (!Array.isArray(currentPermissions)) return false;
  try {
    const payload = decodePayload(token);
    if (!payload) return false;
    const held = new Set(Array.isArray(payload.permissions) ? payload.permissions : []);
    const current = new Set(currentPermissions);
    return held.size !== current.size || [...current].some((permission) => !held.has(permission));
  } catch {
    return false;
  }
};

const RENEWED_KEY = 'tokenRightsRenewedFor';

/**
 * Renew the access token once per changed set of rights, so the APIs see the
 * same rights as the menu. Customer logins and admins are left alone.
 */
export const renewTokenIfRightsChanged = async (userData, refresh) => {
  if (!userData || userData.is_admin || userData.login_channel === 'customer') return false;
  const token = localStorage.getItem('token');
  const refreshTokenValue = localStorage.getItem('refreshToken');
  if (!token || !refreshTokenValue || !tokenRightsDiffer(token, userData.permissions)) return false;
  const signature = [...userData.permissions].sort().join('|');
  try {
    if (sessionStorage.getItem(RENEWED_KEY) === signature) return false;
    sessionStorage.setItem(RENEWED_KEY, signature);
  } catch { /* storage unavailable: renew anyway, still once per page load */ }
  try {
    const result = await refresh(refreshTokenValue);
    if (!result?.access_token) return false;
    localStorage.setItem('token', result.access_token);
    if (result.refresh_token) localStorage.setItem('refreshToken', result.refresh_token);
    return true;
  } catch (error) {
    console.warn('Could not renew the token after a rights change:', error?.message || error);
    return false;
  }
};
